import { Theme, css } from '@emotion/react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import { FullscreenModalProps } from '@components/common/FullscreenModal';
import Overlay from '@components/common/Overlay';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import { IChemiStory } from '@model';
import { MAX_WIDTH, safeArea } from '@styles/css';

const containerCSS = (theme: Theme) => css`
  display: flex;
  justify-content: center;
  align-items: center;

  .modal-header {
    display: flex;
    position: absolute;
    top: max(calc(constant(safe-area-inset-top) + 20px));
    top: max(calc(env(safe-area-inset-top) + 20px));

    right: 20px;
  }
  .content-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 200px;
  }
  .modal-content {
    position: absolute;
    right: 40px;
    display: flex;
    ${Typography.body14};
    color: ${theme.var.white};
    text-align: center;
    line-height: 18.9px;
    margin-top: ${Spacing.medium}px;
    z-index: 1000;
  }
  .card {
    display: flex;
    flex-direction: column;

    .reward-container {
      color: ${theme.colors.main500};
      margin-top: 10px;
      ${Typography.body10};
    }–
  }
`;

const modalCSS = (theme: Theme) =>
  css`
    position: relative;

    border-radius: ${Radius.large}px ${Radius.large}px 0px 0px;
    background-color: ${theme.overlay.bg};
    color: ${theme.var.black};
    width: 100vw;
    max-width: ${MAX_WIDTH}px;
    max-height: 100%;
    transition: 0.3s;
    will-change: transform;
    overflow-x: hidden;
    ${safeArea('bottom', 'padding', Spacing.medium)};

    .theme-title {
      padding: 0 ${Spacing.large}px;
      margin-top: ${Spacing.large}px;
    }

    .modal-container {
      padding: 30px 0 20px 0;
      .content {
        margin-bottom: ${Spacing.medium}px;
      }
    }
  `;

const modalWrapCSS = (theme: Theme) => css`
  position: fixed;
  bottom: 0;
  display: flex;
  align-items: center;

  ${safeArea('top', 'padding', 16)};

  .card-container {
    display: flex;
    margin-top: 10px;
    gap: 10px;
    padding: 0 ${Spacing.large}px;
    padding-bottom: 10px;
  }

  .mission-container {
    margin-top: 40px;
    .mission-title {
      ${Typography.h6};
      margin-bottom: ${Spacing.xhsmall}px;
      margin-left: 16px;
    }
    .msg-count {
      ${Typography.body11};
      margin-left: 16px;
      color: ${theme.var.gray500};
    }
  }

  .mission-card-container {
    display: flex;
    gap: 10px;
    padding: 0 ${Spacing.large}px;
  }
`;

const cardCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  width: 320px;
  height: 100%;
  padding: 20px 12px 16px 16px;
  border-radius: ${Radius.small}px;
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  .step_title {
    ${Typography.body16};
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .title {
    ${Typography.body16};
    font-size: 14px;
    font-weight: 600;
    margin-bottom: ${Spacing.xhsmall}px;
  }

  .desc {
    ${Typography.body11_sentence};
    color: ${theme.var.gray500};
  }

  .progress-container {
    display: flex;
    gap: ${Spacing.small}px;
    margin: ${Spacing.small}px 0;
  }

  .value {
    width: 28px;
    font-size: 10px;
    line-height: 10px;
    color: ${theme.var.gray500};
  }

  .time {
    font-size: 10px;
    line-height: 10px;
    color: ${theme.var.gray500};
  }

  .lock {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    background-color: rgba(255, 255, 255, 0.7);
    width: 100%;
    height: 100%;

    i {
      position: absolute;
      opacity: 0.4;
      top: calc(50% - 15px);
      left: calc(50% - 15px);
    }
  }
`;

type Props = Omit<FullscreenModalProps, 'title'>;

type ChemiStepBottomModalProps = {
  chemiStory?: IChemiStory;
};

const ChemiStepBottomModal = ({
  chemiStory,
  ...props
}: Props & ChemiStepBottomModalProps) => {
  const { open } = props;

  if (!open) {
    return <></>;
  }

  return (
    <Overlay {...props} blocking={true} css={containerCSS}>
      <div css={modalWrapCSS}>
        <div css={modalCSS}>
          <div className="modal-container">
            <SwipeableListContainer>
              <div className="card-container">
                {chemiStory &&
                  chemiStory?.step_set
                    .sort((a, b) => (a.number > b.number ? 1 : -1))
                    .map((item, idx) => {
                      return (
                        <div className="card">
                          <div css={cardCSS} key={`bottom_card_${idx}`}>
                            <h6 className="step_title">{`step ${item.number}.`}</h6>
                            <h6 className="title">{item.title}</h6>
                            <p className="desc">{item.description}</p>
                          </div>
                        </div>
                      );
                    })}
              </div>
            </SwipeableListContainer>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export default ChemiStepBottomModal;
